'use client'
/**
 * @file quiz content with submit button
 */
import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import styles from './index.module.scss';

const SPECIAL_CHARACTERS = /(\?|'|"|<|>|\*|\/|-|=|\+|#|\(|\))/g;

export interface IQuizItem {
  key: string | number;
  /**
   * 答案的 index，-1 代表都可以
   */
  answer: number;
  question: string;
  options: string[];

  /**
   * 额外的输入内容，当前仅支持一个
   */
  hasExtra?: boolean;
  extraIndex?: number;
  onlyExtra?: boolean;
  placeholder?: string;
}

export interface IQuizProps {
  className?: string;
  footerClassName?: string;

  /**
   * 头部内容
   */
  header?: React.ReactNode;

  /**
   * 选项列表
   */
  list: IQuizItem[];

  /*
   * 错误提示文案
   */
  incorrectTip: string;

  /**
   * submit 文案
   */
  submitLabel: string;
  onSubmit: (extra?: string, answer?: (number|string)[]) => void;
}

export const Quiz: React.FC<IQuizProps> = React.memo(
  ({
    className,
    footerClassName,
    header,
    list,
    incorrectTip,
    submitLabel,
    onSubmit
  }) => {
    const [quizSelected, setQuizSelected] = useState<(number|string)[]>(
      new Array(list.length).fill(-1)
    );
    const [extra, setExtra] = useState<string>('');
    const submitEnabled = useMemo(() => {
      return list.every(
        (q, idx) =>
          (q.answer !== -1 && q.answer === quizSelected[idx]) ||
          (q.answer === -1 && !q.hasExtra && (quizSelected[idx] !== -1) ||
          (q.hasExtra && ![0, -1, ''].includes(quizSelected[idx]))) ||
          q.onlyExtra
      );
    }, [list, quizSelected]);

    // 判断是否正确选项
    const isTrueOption = ({
      questionIdx,
      optionIdx,
      questionAnswer,
      hasExtra,
    }) => {
      return (
        (hasExtra && quizSelected[questionIdx] !== -1) ||
        (quizSelected[questionIdx] !== -1 && optionIdx === questionAnswer) ||
        (questionAnswer === -1 && quizSelected[questionIdx] === optionIdx)
      )
    }
    // 判断是否错误选项
    const isFalseOption = ({
      questionIdx,
      optionIdx,
      questionAnswer,
      hasExtra,
    }) => {
      return (
        (
          hasExtra &&
          quizSelected[questionIdx] === ''
        ) ||
        (
          questionAnswer !== -1 &&
          quizSelected[questionIdx] === optionIdx &&
          optionIdx !== questionAnswer
        )
      )
    }

    return (
      <>
        <div className={classNames(styles.content, className)}>
          {header}

          {list.map((q, idx) => (
            <div key={q.key} className={styles.quiz}>
              <div className={styles.question}>{q.question}</div>
              {q.answer !== -1 &&
                quizSelected[idx] !== -1 &&
                quizSelected[idx] !== q.answer && (
                  <div className={styles.incorrect}>{incorrectTip}</div>
                )}
              {q.onlyExtra && (
                <div className={styles.option}>
                  <input
                    className={styles.onlyExtraInput}
                    type="text"
                    value={extra}
                    placeholder={q.placeholder}
                    onChange={(e) =>
                      setExtra(
                        e.target.value
                          .replace(SPECIAL_CHARACTERS, '')
                          .substring(0, 50)
                      )
                    }
                  />
                </div>
              )}
              {q.options.map((o, oIdx) => (
                <div
                  key={oIdx}
                  className={classNames(styles.option, {
                    // selected then right answer should highlight
                    [styles.true]: isTrueOption({
                      questionIdx: idx,
                      optionIdx: oIdx,
                      questionAnswer: q.answer,
                      hasExtra: q.hasExtra,
                    }),
                    // select wrong option
                    [styles.false]: isFalseOption({
                      questionIdx: idx,
                      optionIdx: oIdx,
                      questionAnswer: q.answer,
                      hasExtra: q.hasExtra,
                    })
                  })}
                  onClick={() => {
                    if (!q.hasExtra) {
                      setQuizSelected(
                        quizSelected.map((s, sIdx) => (idx === sIdx ? oIdx : s))
                      )
                    }
                  }}
                >
                  {o}
                  {q.hasExtra &&
                    (
                      <input
                        className={styles.onlyExtraInput}
                        type="text"
                        maxLength={5000}
                        onChange={(e) =>{
                          const value = e.target.value
                            .replace(SPECIAL_CHARACTERS, '')
                            .substring(0, 5000);
                          setQuizSelected(
                            quizSelected.map((s, sIdx) => (idx === sIdx ? value : s))
                          )
                        }}
                      />
                    )}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className={classNames(styles.footer, footerClassName)}>
          <button
            className={styles.submit}
            onClick={() =>
              onSubmit?.(
                list.find((q) => q.hasExtra) ? extra : '',
                quizSelected
              )
            }
            disabled={!submitEnabled}
          >
            {submitLabel}
          </button>
        </div>
      </>
    );
  }
);
